/**
 * OwnershipCard — Tier 0 slice-local component (SCRUM-172).
 *
 * The current-owner panel on the Property detail Owner tab. Purely
 * presentational: it renders the active `OwnershipView` (owner name,
 * share %, effective-from, Active badge, contract name/reference, and a
 * download link when a contract document is attached), or an empty
 * state when there is no current owner. The parent (`OwnerTab`) owns
 * permission gating + modal wiring and passes the action affordances
 * via the `actions` slot, so this component stays data-source- and
 * permission-agnostic (kit-doctrine Tier 0).
 *
 * `share` is a percentage; dates are ISO strings formatted at render.
 */

import * as React from 'react';
import { Badge, Card, Icon } from '@sparta/ui-kit';
import { DetailRow } from './DetailRow.js';
import { formatOwnershipDate } from './ownershipDates.js';
import type { OwnershipView } from '../api/types.js';

export interface OwnershipCardProps {
  /** The current ownership, or `null` when the property has no owner. */
  readonly ownership: OwnershipView | null;
  /**
   * Absolute URL for the contract-document download endpoint
   * (`GET …/ownership/{ownershipId}/document`, a 303→file). The parent
   * builds it from org slug + property/ownership ids; when present and
   * the ownership carries a document, the card renders a real download
   * link. Absent → the document row shows filename + size as plain text.
   */
  readonly contractDocumentHref?: string;
  /**
   * Action affordances rendered in the card footer — the parent passes
   * permission-gated buttons (End / Transfer / Amend share when an
   * owner exists; Add owner for the empty state).
   */
  readonly actions?: React.ReactNode;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function OwnershipCard({
  ownership,
  contractDocumentHref,
  actions,
}: OwnershipCardProps): React.ReactElement {
  if (ownership === null) {
    return (
      <Card padding="md">
        <h2
          className="m-0 mb-1 text-base font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Current owner
        </h2>
        <p
          className="m-0 mb-4 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          No current owner. Record one to track ownership share, transfers,
          and the contract trail.
        </p>
        {actions !== undefined ? (
          <div className="flex flex-wrap gap-2">{actions}</div>
        ) : null}
      </Card>
    );
  }

  return (
    <Card padding="md">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2
          className="m-0 text-base font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Current owner
        </h2>
        {ownership.isActive ? (
          <Badge variant="success" icon="check" size="sm">
            Active
          </Badge>
        ) : null}
      </div>

      <DetailRow label="Owner" value={ownership.ownerName} />
      <DetailRow label="Share" value={`${ownership.sharePercent}%`} />
      <DetailRow
        label="Since"
        value={formatOwnershipDate(ownership.effectiveFrom)}
      />
      {ownership.expiresAt !== undefined ? (
        <DetailRow
          label="Expires"
          value={formatOwnershipDate(ownership.expiresAt)}
        />
      ) : null}
      {ownership.contractName !== undefined ? (
        <DetailRow label="Contract" value={ownership.contractName} />
      ) : null}
      {ownership.contractReference !== undefined ? (
        <DetailRow label="Reference" value={ownership.contractReference} mono />
      ) : null}
      {ownership.contractDocument !== undefined ? (
        <DetailRow
          label="Document"
          value={
            contractDocumentHref !== undefined ? (
              <a
                href={contractDocumentHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 underline"
                style={{ color: 'var(--brand-600)' }}
              >
                <Icon name="arrowUp" size={13} aria-hidden />
                <span>
                  {ownership.contractDocument.filename} (
                  {formatBytes(ownership.contractDocument.sizeBytes)})
                </span>
              </a>
            ) : (
              <span
                className="inline-flex items-center gap-1.5"
                style={{ color: 'var(--fg-secondary)' }}
              >
                <Icon name="arrowUp" size={13} aria-hidden />
                {ownership.contractDocument.filename} (
                {formatBytes(ownership.contractDocument.sizeBytes)})
              </span>
            )
          }
        />
      ) : null}

      {actions !== undefined ? (
        <div className="mt-4 flex flex-wrap gap-2">{actions}</div>
      ) : null}
    </Card>
  );
}
